import { useMemo, useState } from "react";
import { useStudio } from "./StudioContext";
import { StudioPage, Panel, SectionLabel, Metric, Loading, cx } from "./StudioShared";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { ScanSearch, Users, Lightbulb, Layers, ArrowRight, ChevronDown, Info, Download, Upload, Cpu } from "lucide-react";

const GOLD = "#D4AF37";
const tip = { background: "#0A1128", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", fontSize: 12, borderRadius: 2 };

const STEPS = [
  {
    icon: ScanSearch, name: "Analyze", desc: "Understand the mission & context",
    what: "AIPOS reads the incoming mission and builds a structured understanding of the business before any content is produced.",
    inputs: "Business / mission context, industry, goal, target audience, tone, keywords & location, plus any linked Client/Project knowledge (the Digital Brain).",
    outputs: "A normalized mission profile — the single shared context every downstream stage reasons from.",
    involved: "Business Intelligence layer + mission profiler.",
  },
  {
    icon: Users, name: "Select", desc: "Choose the most relevant strategists",
    what: "Picks which of the available strategists are relevant to this specific mission, instead of running all of them on every job.",
    inputs: "The analyzed mission profile — especially industry, goal and audience.",
    outputs: "A focused short-list of the strategists best suited to this mission.",
    involved: "Strategist registry + Intelligence Router.",
  },
  {
    icon: Lightbulb, name: "Advise", desc: "Each strategist reasons in its domain",
    what: "Every selected strategist reasons within its own area of expertise and returns recommendations with a confidence score.",
    inputs: "The mission profile combined with each strategist's domain knowledge.",
    outputs: "Per-strategist recommendations, each with its own confidence level.",
    involved: "The individual strategists + the reasoning model via the active provider/gateway.",
  },
  {
    icon: Layers, name: "Synthesize", desc: "One unified Growth Roadmap",
    what: "Merges every strategist's advice into one coherent, de-duplicated plan the business can act on.",
    inputs: "All per-strategist recommendations and their confidences.",
    outputs: "A single unified Growth Roadmap plus an overall confidence score, saved to the mission.",
    involved: "Synthesis engine + mission store.",
  },
];

const DETAIL_ROWS = [
  { key: "what", label: "What is this stage?", icon: Info },
  { key: "inputs", label: "Inputs it uses", icon: Download },
  { key: "outputs", label: "What it produces", icon: Upload },
  { key: "involved", label: "AIPOS parts involved", icon: Cpu },
];

export default function MissionIntelligence() {
  const { health, missions, analytics, strategists, providers } = useStudio();
  const [active, setActive] = useState(null);

  const trend = useMemo(() => {
    if (!missions) return [];
    return [...missions]
      .filter((m) => m.document?.confidence != null && m.created_at)
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      .map((m, i) => ({ i: i + 1, conf: Math.round((m.document.confidence || 0) * 100), org: m.org_name }));
  }, [missions]);

  if (!health || !missions) return <StudioPage title="Mission Intelligence" question="How does the reasoning engine work?"><Loading /></StudioPage>;
  const mi = analytics?.mission_intelligence || {};

  return (
    <StudioPage title="Mission Intelligence" question="How does the reasoning engine work?" testid="mi-overview">
      {/* Pipeline */}
      <SectionLabel className="mb-2">The Reasoning Engine</SectionLabel>
      <Panel className="p-6 mb-6" data-testid="mi-pipeline">
        <div className="flex flex-col md:flex-row items-stretch gap-2">
          {STEPS.map((s, i) => {
            const isActive = active === i;
            return (
              <div key={s.name} className="flex-1 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActive(isActive ? null : i)}
                  aria-expanded={isActive}
                  className={cx(
                    "flex-1 border rounded-sm p-4 text-center transition-colors cursor-pointer w-full",
                    isActive ? "bg-gold/10 border-gold/60" : "bg-navy border-white/10 hover:border-gold/40"
                  )}
                  data-testid={`pipeline-step-${s.name.toLowerCase()}`}
                >
                  <span className="inline-flex w-10 h-10 border border-gold/40 rounded-sm items-center justify-center text-gold mb-2"><s.icon size={18} /></span>
                  <div className="text-white text-sm font-medium">{s.name}</div>
                  <div className="text-slate-500 text-xs mt-1">{s.desc}</div>
                  <div className={cx("flex items-center justify-center gap-1 text-[10px] mt-2 transition-colors", isActive ? "text-gold" : "text-slate-500")}>
                    {isActive ? "Hide" : "Details"} <ChevronDown size={11} className={cx("transition-transform", isActive && "rotate-180")} />
                  </div>
                </button>
                {i < STEPS.length - 1 && <ArrowRight size={18} className="text-slate-600 shrink-0 rotate-90 md:rotate-0" />}
              </div>
            );
          })}
        </div>

        {active !== null && (
          <div className="mt-4 border border-gold/25 bg-navy rounded-sm p-5" data-testid="pipeline-detail">
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex w-8 h-8 border border-gold/40 rounded-sm items-center justify-center text-gold">{(() => { const I = STEPS[active].icon; return <I size={16} />; })()}</span>
              <div className="text-white font-medium">Stage {active + 1} of 4 — {STEPS[active].name}</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {DETAIL_ROWS.map((row) => (
                <div key={row.key} className="flex gap-3" data-testid={`pipeline-detail-${row.key}`}>
                  <span className="shrink-0 mt-0.5 text-gold"><row.icon size={15} /></span>
                  <div>
                    <div className="text-slate-400 text-[11px] uppercase tracking-wide mb-0.5">{row.label}</div>
                    <div className="text-slate-200 text-sm leading-relaxed">{STEPS[active][row.key]}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Panel>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Confidence trend */}
        <Panel className="lg:col-span-2 p-5" data-testid="mi-confidence-trend">
          <SectionLabel className="mb-4">Confidence Trend</SectionLabel>
          {trend.length === 0 ? <div className="h-[240px] flex items-center justify-center text-slate-600 text-sm">Confidence appears as mission plans are generated.</div> : (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={trend} margin={{ left: -20 }}>
                <defs><linearGradient id="miconf" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={GOLD} stopOpacity={0.5} /><stop offset="95%" stopColor={GOLD} stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="i" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={{ stroke: "#ffffff20" }} />
                <YAxis domain={[0, 100]} tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={{ stroke: "#ffffff20" }} />
                <Tooltip contentStyle={tip} formatter={(v, n, p) => [`${v}%`, p?.payload?.org || "confidence"]} />
                <Area type="monotone" dataKey="conf" stroke={GOLD} strokeWidth={2} fill="url(#miconf)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Panel>

        {/* System health matrix */}
        <div>
          <SectionLabel className="mb-2">System Health</SectionLabel>
          <div className="grid grid-cols-2 gap-3" data-testid="mi-health-matrix">
            <Metric label="Engine" value={health.enabled ? "Online" : "Off"} status={health.enabled ? "healthy" : "error"} />
            <Metric label="Provider" value={health.provider_available ? "Ready" : "None"} status={health.provider_available ? "healthy" : "warning"} />
            <Metric label="Strategists" value={strategists?.length ?? health.strategists} />
            <Metric label="Model" value={(health.default_model || "—").replace("gpt-", "")} sub={health.default_provider} />
            <Metric label="Avg Conf" value={mi.avg_confidence != null ? `${Math.round(mi.avg_confidence * 100)}%` : "—"} />
            <Metric label="Routing" value={(providers?.routing_policy || health.routing_policy || "—")} />
          </div>
        </div>
      </div>
    </StudioPage>
  );
}
